import '@ant-design/v5-patch-for-react-19';

import * as React from 'react';

import { StyleProvider } from '@ant-design/cssinjs';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, message, theme } from 'antd';
import daDK from 'antd/locale/da_DK';
import deDE from 'antd/locale/de_DE';
import elGR from 'antd/locale/el_GR';
import enUS from 'antd/locale/en_US';
import fiFI from 'antd/locale/fi_FI';
import frFR from 'antd/locale/fr_FR';
import idID from 'antd/locale/id_ID';
import itIT from 'antd/locale/it_IT';
import jaJP from 'antd/locale/ja_JP';
import koKR from 'antd/locale/ko_KR';
import msMY from 'antd/locale/ms_MY';
import nbNO from 'antd/locale/nb_NO';
import nlBE from 'antd/locale/nl_BE';
import nlNL from 'antd/locale/nl_NL';
import plPL from 'antd/locale/pl_PL';
import svSE from 'antd/locale/sv_SE';
import thTH from 'antd/locale/th_TH';
import viVN from 'antd/locale/vi_VN';
import zhTW from 'antd/locale/zh_TW';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { HashRouter, Route, Routes } from 'react-router';

import AlertsAndProgress from '@core/app/components/dialogs/AlertAndProgress';
import Dialog from '@core/app/components/dialogs/Dialog';
import { AlertProgressContextProvider } from '@core/app/contexts/AlertProgressContext';
import { DialogContextProvider } from '@core/app/contexts/DialogContext';
import Beambox from '@core/app/pages/Beambox';
import Error from '@core/app/pages/Error';
import FacebookOAuth from '@core/app/pages/FacebookOAuth';
import FluxIdLogin from '@core/app/pages/FluxIdLogin';
import GoogleOAuth from '@core/app/pages/GoogleOAuth';
import Home from '@core/app/pages/Home';
import ConnectEthernet from '@core/app/pages/InitializeMachine/ConnectEthernet';
import ConnectMachineIp from '@core/app/pages/InitializeMachine/ConnectMachineIp';
import ConnectUsb from '@core/app/pages/InitializeMachine/ConnectUsb';
import ConnectWiFi from '@core/app/pages/InitializeMachine/ConnectWiFi';
import ConnectWired from '@core/app/pages/InitializeMachine/ConnectWired';
import PromarkSettings from '@core/app/pages/InitializeMachine/PromarkSettings';
import SelectConnectionType from '@core/app/pages/InitializeMachine/SelectConnectionType';
import SelectMachineModel from '@core/app/pages/InitializeMachine/SelectMachineModel';
import SelectPromarkLaserSource from '@core/app/pages/InitializeMachine/SelectPromarkLaserSource';
import Welcome from '@core/app/pages/Welcome';
import Logger from '@core/helpers/logger';
import { queryClient } from '@core/helpers/query';
import type { StorageKey } from '@core/interfaces/IStorage';

import ErrorBoundaryFallback from './components/ErrorBoundaryFallback';
import { DEFAULT_CONFIG, useSettingStore } from './components/settings/shared/hooks/useSettingStore';
import useAutoConnect from './hooks/useAutoConnect';

const { defaultAlgorithm } = theme;

const localeMap = {
  da_DK: daDK,
  'de-DE': deDE,
  el_GR: elGR,
  'en-US': enUS,
  fi_FI: fiFI,
  'fr-FR': frFR,
  id_ID: idID,
  'it-IT': itIT,
  'ja-JP': jaJP,
  'ko-KR': koKR,
  ms_MY: msMY,
  nb_NO: nbNO,
  'nl-BE': nlBE,
  'nl-NL': nlNL,
  'pl-PL': plPL,
  sv_SE: svSE,
  th_TH: thTH,
  vi_VN: viVN,
  'zh-TW': zhTW,
};

console.log('Loading language', navigator.language);

const App = (): React.JSX.Element => {
  const [messageApi, contextHolder] = message.useMessage();
  const { getConfig } = useSettingStore();

  useAutoConnect();

  Object.keys(DEFAULT_CONFIG).forEach((key) => {
    if (key === 'enable-sentry') return;

    getConfig(key as StorageKey);
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary
        FallbackComponent={ErrorBoundaryFallback}
        onError={(error, info) => {
          // Log errors to Logger utility for debugging
          const logger = Logger('error-boundary', 100);

          logger.append({
            componentStack: info.componentStack,
            message: error.message,
            stack: error.stack,
            timestamp: new Date().toISOString(),
          });

          // Log React 19 context errors specifically for development
          if (error.message.includes('use() called outside provider')) {
            console.error('[Context Error]', error.message, info.componentStack);
          }
        }}
      >
        <AlertProgressContextProvider messageApi={messageApi}>
          <DialogContextProvider>
            <ConfigProvider
              locale={(localeMap as any)[navigator.language]}
              theme={{
                algorithm: defaultAlgorithm,
                components: {
                  Message: {
                    // set this value because zIndex of windows custom title bar is 99999
                    zIndexPopup: 100000,
                  },
                },
                token: {
                  fontFamily:
                    '"Helvetica Neue", "HelveticaNeue-Light", "Helvetica Neue Light", "Open Sans", Helvetica, Arial, "Lucida Grande", sans-serif, "Microsoft JhengHei", "微軟正黑體"',
                  screenMD: 601,
                  screenMDMin: 601,
                  screenSMMax: 600,
                },
              }}
            >
              <StyleProvider hashPriority="low">
                <Dialog />
                <AlertsAndProgress />
                {contextHolder}
                <HashRouter>
                  <Routes>
                    <Route element={<GoogleOAuth />} path="/google-auth" />
                    <Route element={<FacebookOAuth />} path="/fb-auth" />
                    <Route element={<SelectConnectionType />} path="/initialize/connect/select-connection-type" />
                    <Route element={<SelectMachineModel />} path="/initialize/connect/select-machine-model" />
                    <Route element={<ConnectMachineIp />} path="/initialize/connect/connect-machine-ip" />
                    <Route element={<ConnectUsb />} path="/initialize/connect/connect-usb" />
                    <Route element={<ConnectWiFi />} path="/initialize/connect/connect-wi-fi" />
                    <Route element={<ConnectWired />} path="/initialize/connect/connect-wired" />
                    <Route element={<ConnectEthernet />} path="/initialize/connect/connect-ethernet" />
                    <Route element={<FluxIdLogin />} path="/initialize/connect/flux-id-login" />
                    <Route
                      element={<SelectPromarkLaserSource />}
                      path="/initialize/connect/select-promark-laser-source"
                    />
                    <Route element={<PromarkSettings />} path="/initialize/connect/promark-settings" />
                    <Route element={<Beambox />} path="/studio/beambox" />
                    <Route element={<Welcome />} path="/studio/welcome" />
                    <Route element={<Error />} path="/error/*" />
                    <Route element={<Home />} path="*" />
                  </Routes>
                </HashRouter>
              </StyleProvider>
            </ConfigProvider>
          </DialogContextProvider>
        </AlertProgressContextProvider>
      </ErrorBoundary>
      {/* Useful when debugging React Query */}
      {/* {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
    </QueryClientProvider>
  );
};

const router = (container: DocumentFragment | Element): void => {
  createRoot(container).render(<App />);
};

export default router;
